/**
 * RosterManagementPage.js
 * View and manage player rosters for a league.
 * Accessed from Dashboard "Roster" button on each league card.
 *
 * Features:
 *  - Players grouped by team (+ unassigned section)
 *  - Search / team filter
 *  - Move player to a different team or unassign
 *  - Add player manually (creates Registration + Player record)
 *  - Remove player from roster
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import './RosterManagementPage.css';

// ─── Helper components ─────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type === 'error' ? '#dc2626' : '#16a34a';
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: bg, color: '#fff', padding: '10px 20px', borderRadius: 8,
      fontWeight: 600, fontSize: '0.9rem', zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    }}>
      {toast.message}
    </div>
  );
}

function RoleBadge({ role }) {
  const isCaptain = role === 'captain';
  return (
    <span className={`roster-role-badge ${isCaptain ? 'captain' : 'player'}`}>
      {isCaptain ? 'Captain' : 'Player'}
    </span>
  );
}

function PaymentBadge({ status }) {
  const cfg = {
    paid:         { label: 'Paid',         cls: 'paid' },
    pending:      { label: 'Pending $',    cls: 'pending' },
    refunded:     { label: 'Refunded',     cls: 'refunded' },
    not_required: { label: '',             cls: '' },
  };
  const c = cfg[status] || cfg.not_required;
  if (!c.label) return null;
  return <span className={`roster-payment-badge ${c.cls}`}>{c.label}</span>;
}

// ─── Add Player Modal ─────────────────────────────────────────────────────

function AddPlayerModal({ teams, leagueId, onClose, onAdded, showToast }) {
  const [form, setForm] = useState({
    registrantName: '',
    registrantEmail: '',
    registrantPhone: '',
    teamId: '',
    role: 'player',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.registrantName.trim()) { setError('Name is required'); return; }
    if (!form.registrantEmail.trim()) { setError('Email is required'); return; }

    setSaving(true);
    try {
      const result = await api.addRosterPlayer(leagueId, {
        registrantName: form.registrantName.trim(),
        registrantEmail: form.registrantEmail.trim(),
        registrantPhone: form.registrantPhone.trim() || null,
        teamId: form.teamId || null,
        role: form.role,
      });
      onAdded(result.registration);
      showToast('Player added to roster');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add player');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="roster-modal-overlay" onClick={onClose}>
      <div className="roster-modal" onClick={e => e.stopPropagation()}>
        <div className="roster-modal-header">
          <h3>Add Player to Roster</h3>
          <button className="roster-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="roster-modal-body">
          {error && <div className="roster-error-msg">{error}</div>}

          <label className="roster-label">
            Name <span className="roster-required">*</span>
            <input
              className="roster-input"
              type="text"
              value={form.registrantName}
              onChange={e => setForm(p => ({ ...p, registrantName: e.target.value }))}
              placeholder="Full name"
              autoFocus
            />
          </label>

          <label className="roster-label">
            Email <span className="roster-required">*</span>
            <input
              className="roster-input"
              type="email"
              value={form.registrantEmail}
              onChange={e => setForm(p => ({ ...p, registrantEmail: e.target.value }))}
              placeholder="email@example.com"
            />
          </label>

          <label className="roster-label">
            Phone
            <input
              className="roster-input"
              type="tel"
              value={form.registrantPhone}
              onChange={e => setForm(p => ({ ...p, registrantPhone: e.target.value }))}
              placeholder="Optional"
            />
          </label>

          <label className="roster-label">
            Assign to Team
            <select
              className="roster-select"
              value={form.teamId}
              onChange={e => setForm(p => ({ ...p, teamId: e.target.value }))}
            >
              <option value="">— Unassigned —</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>

          <label className="roster-label">
            Role
            <select
              className="roster-select"
              value={form.role}
              onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
            >
              <option value="player">Player</option>
              <option value="captain">Captain</option>
            </select>
          </label>

          <div className="roster-modal-actions">
            <button type="button" className="roster-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="roster-btn-primary" disabled={saving}>
              {saving ? 'Adding...' : 'Add Player'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Player Row ─────────────────────────────────────────────────────────────

function PlayerRow({ player, teams, currentTeamId, leagueId, onMoved, onRemoved, showToast }) {
  const [moving, setMoving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showMoveSelect, setShowMoveSelect] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');

  const handleMove = async () => {
    setMoving(true);
    try {
      await api.moveRosterPlayer(leagueId, player.id, moveTarget || null);
      onMoved(player.id, moveTarget || null);
      showToast(`${player.registrantName} moved`);
      setShowMoveSelect(false);
      setMoveTarget('');
    } catch (err) {
      showToast(err.message || 'Failed to move player', 'error');
    } finally {
      setMoving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${player.registrantName} from the roster?`)) return;
    setRemoving(true);
    try {
      await api.removeRosterPlayer(leagueId, player.id);
      onRemoved(player.id);
      showToast(`${player.registrantName} removed`);
    } catch (err) {
      showToast(err.message || 'Failed to remove player', 'error');
    } finally {
      setRemoving(false);
    }
  };

  const availableTeams = teams.filter(t => t.id !== currentTeamId);

  return (
    <div className="roster-player-row">
      <div className="roster-player-info">
        <div className="roster-player-name">
          {player.registrantName}
          <RoleBadge role={player.role} />
          <PaymentBadge status={player.paymentStatus} />
        </div>
        <div className="roster-player-contact">
          {player.registrantEmail}
          {player.registrantPhone && (
            <span className="roster-player-phone">{player.registrantPhone}</span>
          )}
        </div>
      </div>

      <div className="roster-player-actions">
        {showMoveSelect ? (
          <div className="roster-move-controls">
            <select
              className="roster-select-sm"
              value={moveTarget}
              onChange={e => setMoveTarget(e.target.value)}
            >
              <option value="">— Unassign —</option>
              {availableTeams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button
              className="roster-btn-xs-primary"
              onClick={handleMove}
              disabled={moving}
            >
              {moving ? '...' : 'Move'}
            </button>
            <button
              className="roster-btn-xs-secondary"
              onClick={() => { setShowMoveSelect(false); setMoveTarget(''); }}
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            <button
              className="roster-btn-xs-secondary"
              onClick={() => setShowMoveSelect(true)}
              title="Move to another team"
            >
              Move
            </button>
            <button
              className="roster-btn-xs-danger"
              onClick={handleRemove}
              disabled={removing}
              title="Remove from roster"
            >
              {removing ? '...' : 'Remove'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Team Card ────────────────────────────────────────────────────────────────

function TeamCard({ team, allTeams, leagueId, onMoved, onRemoved, showToast, searchQuery }) {
  const filtered = team.players.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.registrantName.toLowerCase().includes(q) ||
      p.registrantEmail.toLowerCase().includes(q)
    );
  });

  if (filtered.length === 0 && searchQuery) return null;

  return (
    <div className="roster-team-card">
      <div className="roster-team-header">
        <span className="roster-team-name">{team.name}</span>
        {team.groupNumber === 2 && (
          <span className="roster-group-badge">Group 2</span>
        )}
        <span className="roster-player-count">
          {team.players.length} {team.players.length === 1 ? 'player' : 'players'}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="roster-empty-team">No players assigned yet.</div>
      ) : (
        <div className="roster-players-list">
          {filtered.map(p => (
            <PlayerRow
              key={p.id}
              player={p}
              teams={allTeams}
              currentTeamId={team.id}
              leagueId={leagueId}
              onMoved={onMoved}
              onRemoved={onRemoved}
              showToast={showToast}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function RosterManagementPage({ leagueId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [leagueName, setLeagueName] = useState('');
  const [rosterData, setRosterData] = useState(null); // { teams, unassigned, formExists, formStatus, registrationMode }
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('all'); // 'all' | 'unassigned' | team.id

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const [leagueResult, rosterResult] = await Promise.all([
        api.getLeague(leagueId),
        api.getRoster(leagueId),
      ]);
      setLeagueName(leagueResult.league?.name || leagueResult.name || 'League');
      setRosterData(rosterResult);
    } catch (err) {
      showToast(err.message || 'Failed to load roster', 'error');
    } finally {
      setLoading(false);
    }
  }, [leagueId, showToast]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // ── Roster mutation helpers ──────────────────────────────────────────────

  // After a move, update local state instead of re-fetching
  const handleMoved = useCallback((registrationId, toTeamId) => {
    setRosterData(prev => {
      if (!prev) return prev;

      // Find the player in existing teams or unassigned
      let movedPlayer = null;
      let fromTeamId = null;

      // Search teams
      for (const team of prev.teams) {
        const idx = team.players.findIndex(p => p.id === registrationId);
        if (idx !== -1) {
          movedPlayer = team.players[idx];
          fromTeamId = team.id;
          break;
        }
      }

      // Search unassigned
      if (!movedPlayer) {
        const idx = prev.unassigned.findIndex(p => p.id === registrationId);
        if (idx !== -1) {
          movedPlayer = prev.unassigned[idx];
        }
      }

      if (!movedPlayer) return prev;

      // Remove from source
      const newTeams = prev.teams.map(team => ({
        ...team,
        players: team.id === fromTeamId
          ? team.players.filter(p => p.id !== registrationId)
          : team.players,
      }));
      const newUnassigned = fromTeamId
        ? prev.unassigned
        : prev.unassigned.filter(p => p.id !== registrationId);

      // Add to destination
      if (toTeamId) {
        const destIdx = newTeams.findIndex(t => t.id === toTeamId);
        if (destIdx !== -1) {
          newTeams[destIdx] = {
            ...newTeams[destIdx],
            players: [...newTeams[destIdx].players, movedPlayer].sort((a, b) =>
              a.registrantName.localeCompare(b.registrantName)
            ),
          };
        }
      } else {
        newUnassigned.push(movedPlayer);
        newUnassigned.sort((a, b) => a.registrantName.localeCompare(b.registrantName));
      }

      return { ...prev, teams: newTeams, unassigned: newUnassigned };
    });
  }, []);

  const handleRemoved = useCallback((registrationId) => {
    setRosterData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        teams: prev.teams.map(team => ({
          ...team,
          players: team.players.filter(p => p.id !== registrationId),
        })),
        unassigned: prev.unassigned.filter(p => p.id !== registrationId),
      };
    });
  }, []);

  const handleAdded = useCallback((registration) => {
    // Re-fetch to get the full player row including Player record
    fetchRoster();
  }, [fetchRoster]);

  // ── Derived stats ─────────────────────────────────────────────────────────

  const allTeams = rosterData?.teams || [];
  const unassigned = rosterData?.unassigned || [];
  const totalPlayers = allTeams.reduce((sum, t) => sum + t.players.length, 0) + unassigned.length;
  const captainCount = [
    ...allTeams.flatMap(t => t.players),
    ...unassigned,
  ].filter(p => p.role === 'captain').length;

  // ── Filter logic ──────────────────────────────────────────────────────────

  // Which teams to show based on team filter
  const visibleTeams = teamFilter === 'all'
    ? allTeams
    : teamFilter === 'unassigned'
      ? []
      : allTeams.filter(t => t.id === teamFilter);

  const showUnassigned = teamFilter === 'all' || teamFilter === 'unassigned';

  const filteredUnassigned = unassigned.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.registrantName.toLowerCase().includes(q) ||
      p.registrantEmail.toLowerCase().includes(q)
    );
  });

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="roster-container">
        <div className="roster-loading">Loading roster...</div>
      </div>
    );
  }

  return (
    <div className="roster-container">
      <Toast toast={toast} />

      {/* Header */}
      <div className="roster-header">
        <button className="roster-back-btn" onClick={onBack}>← Back</button>
        <div className="roster-header-title">
          <h2>Roster</h2>
          <span className="roster-league-name">{leagueName}</span>
        </div>
        <button
          className="roster-btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          + Add Player
        </button>
      </div>

      {/* Stats bar */}
      <div className="roster-stats-bar">
        <div className="roster-stat">
          <span className="roster-stat-value">{totalPlayers}</span>
          <span className="roster-stat-label">Total Players</span>
        </div>
        <div className="roster-stat">
          <span className="roster-stat-value">{captainCount}</span>
          <span className="roster-stat-label">Captains</span>
        </div>
        <div className="roster-stat">
          <span className="roster-stat-value">{totalPlayers - captainCount}</span>
          <span className="roster-stat-label">Players</span>
        </div>
        <div className="roster-stat">
          <span className="roster-stat-value">{unassigned.length}</span>
          <span className="roster-stat-label">Unassigned</span>
        </div>
        <div className="roster-stat">
          <span className="roster-stat-value">{allTeams.length}</span>
          <span className="roster-stat-label">Teams</span>
        </div>
        {rosterData?.formStatus && (
          <div className="roster-stat">
            <span className={`roster-form-status-badge ${rosterData.formStatus}`}>
              Reg: {rosterData.formStatus.charAt(0).toUpperCase() + rosterData.formStatus.slice(1)}
            </span>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="roster-filter-bar">
        <input
          className="roster-search-input"
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <select
          className="roster-select"
          value={teamFilter}
          onChange={e => setTeamFilter(e.target.value)}
        >
          <option value="all">All Teams</option>
          <option value="unassigned">Unassigned</option>
          {allTeams.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {(searchQuery || teamFilter !== 'all') && (
          <button
            className="roster-btn-clear"
            onClick={() => { setSearchQuery(''); setTeamFilter('all'); }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Empty state */}
      {totalPlayers === 0 ? (
        <div className="roster-empty-state">
          <div className="roster-empty-icon">👥</div>
          <h3>No players yet</h3>
          <p>Add players manually or open registration so they can sign up themselves.</p>
          <button className="roster-btn-primary" onClick={() => setShowAddModal(true)}>
            + Add First Player
          </button>
        </div>
      ) : (
        <div className="roster-content">

          {/* Team cards */}
          {visibleTeams.map(team => (
            <TeamCard
              key={team.id}
              team={team}
              allTeams={allTeams}
              leagueId={leagueId}
              onMoved={handleMoved}
              onRemoved={handleRemoved}
              showToast={showToast}
              searchQuery={searchQuery}
            />
          ))}

          {/* Unassigned section */}
          {showUnassigned && (unassigned.length > 0 || teamFilter === 'unassigned') && (
            <div className="roster-team-card roster-unassigned-card">
              <div className="roster-team-header">
                <span className="roster-team-name roster-unassigned-label">Unassigned</span>
                <span className="roster-player-count">
                  {unassigned.length} {unassigned.length === 1 ? 'player' : 'players'}
                </span>
              </div>

              {filteredUnassigned.length === 0 ? (
                <div className="roster-empty-team">
                  {searchQuery ? 'No unassigned players match your search.' : 'No unassigned players.'}
                </div>
              ) : (
                <div className="roster-players-list">
                  {filteredUnassigned.map(p => (
                    <PlayerRow
                      key={p.id}
                      player={p}
                      teams={allTeams}
                      currentTeamId={null}
                      leagueId={leagueId}
                      onMoved={handleMoved}
                      onRemoved={handleRemoved}
                      showToast={showToast}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* No results for search/filter */}
          {searchQuery && visibleTeams.every(t =>
            t.players.filter(p => {
              const q = searchQuery.toLowerCase();
              return p.registrantName.toLowerCase().includes(q) || p.registrantEmail.toLowerCase().includes(q);
            }).length === 0
          ) && filteredUnassigned.length === 0 && (
            <div className="roster-no-results">
              No players match "{searchQuery}".
            </div>
          )}
        </div>
      )}

      {/* Add player modal */}
      {showAddModal && (
        <AddPlayerModal
          teams={allTeams}
          leagueId={leagueId}
          onClose={() => setShowAddModal(false)}
          onAdded={handleAdded}
          showToast={showToast}
        />
      )}
    </div>
  );
}
